import React from 'react';
import { FileText } from 'lucide-react';
import { FICHA } from '../urbanismo/FichaTrazada';

/**
 * El mapa vivo del héroe: el mismo trozo de territorio visto con los ojos de cada
 * producto. En «urbanismo» se colorean las zonas del plan, se traza la parcela y su
 * ficha se escribe con la cita de cada cifra; en «territorio» sobre el mismo
 * parcelario se dibujan relieve, río, espacio protegido y línea eléctrica.
 * Cada vista se monta con su `key`, así las animaciones (.anim-*) se repiten al
 * cambiar de pestaña. Valores ilustrativos: la ficha lo dice.
 */

// Manzanas del parcelario (viewBox 520×520): la base común a las dos vistas.
const MANZANAS = [
  { x: 20, y: 20, an: 150, al: 110, familia: 'f-residencial' },
  { x: 186, y: 20, an: 110, al: 110, familia: 'f-dotacio' },
  { x: 312, y: 20, an: 190, al: 60, familia: 'f-sistema' },
  { x: 312, y: 96, an: 190, al: 34, familia: 'f-residencial' },
  { x: 20, y: 146, an: 96, al: 150, familia: 'f-activitat' },
  { x: 132, y: 146, an: 200, al: 90, familia: 'f-residencial' },
  { x: 132, y: 252, an: 200, al: 44, familia: 'f-sistema' },
  { x: 348, y: 146, an: 154, al: 150, familia: 'f-urbanitzable' },
  { x: 20, y: 312, an: 240, al: 190, familia: 'f-no-urbanitzable' },
  { x: 276, y: 312, an: 226, al: 190, familia: 'f-no-urbanitzable' },
];

// Parcelas dentro de las manzanas (líneas finas del catastro).
const LINDES = [
  [182, 146, 182, 236], [232, 146, 232, 236], [282, 146, 282, 236], [132, 191, 332, 191],
  [70, 20, 70, 130], [120, 20, 120, 130], [20, 75, 170, 75],
  [407, 20, 407, 80], [68, 146, 68, 296], [425, 146, 425, 296],
  [140, 312, 140, 502], [390, 312, 390, 502], [276, 410, 502, 410],
];

const PARCELA = { x: 232, y: 146, an: 50, al: 45 };

const Parcelario = () => (
  <g fill="none">
    {MANZANAS.map((m, i) => (
      <rect
        key={`${m.x}-${m.y}`}
        className="anim-traza"
        style={{ '--d': `${i * 40}ms` }}
        pathLength="1000"
        x={m.x}
        y={m.y}
        width={m.an}
        height={m.al}
        stroke="oklch(var(--foreground) / 0.28)"
        strokeWidth="1"
      />
    ))}
    {LINDES.map(([x1, y1, x2, y2], i) => (
      <line
        key={`${x1}-${y1}-${x2}`}
        className="anim-traza"
        style={{ '--d': `${300 + i * 30}ms` }}
        pathLength="1000"
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="oklch(var(--foreground) / 0.16)"
        strokeWidth="0.8"
      />
    ))}
  </g>
);

const Parcela = ({ retraso }) => (
  <g>
    <rect
      className="anim-aparece"
      style={{ '--d': `${retraso}ms`, '--o': 1 }}
      x={PARCELA.x}
      y={PARCELA.y}
      width={PARCELA.an}
      height={PARCELA.al}
      fill="oklch(var(--ember) / 0.25)"
    />
    <rect
      className="anim-traza"
      style={{ '--d': `${retraso}ms` }}
      pathLength="1000"
      x={PARCELA.x}
      y={PARCELA.y}
      width={PARCELA.an}
      height={PARCELA.al}
      fill="none"
      stroke="oklch(var(--ember))"
      strokeWidth="3"
    />
    <line
      className="anim-traza"
      style={{ '--d': `${retraso + 350}ms` }}
      pathLength="1000"
      x1={PARCELA.x + PARCELA.an}
      y1={PARCELA.y + 22}
      x2="530"
      y2={PARCELA.y + 22}
      stroke="oklch(var(--ember))"
      strokeWidth="1.5"
      strokeDasharray="4 4"
    />
  </g>
);

const VistaUrbanismo = () => (
  <g>
    {MANZANAS.map((m, i) => (
      <rect
        key={`z-${m.x}-${m.y}`}
        className="anim-aparece"
        style={{ '--d': `${150 + i * 60}ms`, '--o': m.familia === 'f-no-urbanitzable' ? 0.32 : 0.5 }}
        x={m.x}
        y={m.y}
        width={m.an}
        height={m.al}
        fill={`var(--${m.familia})`}
      />
    ))}
    <text x="137" y="160" fontSize="11" fill="oklch(var(--foreground) / 0.7)" fontFamily="Inter, sans-serif" className="anim-aparece" style={{ '--d': '700ms' }}>
      13b
    </text>
    <Parcela retraso={800} />
  </g>
);

const TORRES = [
  [40, 40], [132, 80], [224, 120], [316, 160], [408, 200], [500, 240],
];

const VistaTerritorio = () => (
  <g fill="none">
    {/* Relieve: curvas de nivel alrededor de un cerro */}
    {[150, 118, 88, 60, 34].map((r, i) => (
      <ellipse
        key={r}
        className="anim-traza"
        style={{ '--d': `${100 + i * 90}ms` }}
        pathLength="1000"
        cx="430"
        cy="440"
        rx={r * 1.25}
        ry={r}
        stroke="oklch(var(--solar) / 0.55)"
        strokeWidth="1"
      />
    ))}
    {/* Espacio protegido (Red Natura 2000) */}
    <path
      className="anim-aparece"
      style={{ '--d': '500ms', '--o': 1 }}
      d="M20 330 C80 300 170 320 250 360 C280 420 240 500 160 505 L20 505 Z"
      fill="oklch(var(--disponible) / 0.16)"
    />
    <path
      className="anim-traza"
      style={{ '--d': '500ms' }}
      pathLength="1000"
      d="M20 330 C80 300 170 320 250 360 C280 420 240 500 160 505 L20 505 Z"
      stroke="oklch(var(--disponible))"
      strokeWidth="1.5"
      strokeDasharray="6 4"
    />
    {/* Río */}
    <path
      className="anim-traza"
      style={{ '--d': '300ms' }}
      pathLength="1000"
      d="M-10 250 C60 270 110 330 170 320 S300 290 360 330 S470 380 530 370"
      stroke="var(--f-dotacio)"
      strokeWidth="3.5"
      strokeLinecap="round"
    />
    {/* Línea eléctrica con sus apoyos */}
    <polyline
      className="anim-traza"
      style={{ '--d': '700ms' }}
      pathLength="1000"
      points={TORRES.map((t) => t.join(',')).join(' ')}
      stroke="oklch(var(--foreground) / 0.7)"
      strokeWidth="1.5"
    />
    {TORRES.map(([x, y], i) => (
      <rect
        key={`${x}-${y}`}
        className="anim-aparece"
        style={{ '--d': `${750 + i * 80}ms`, '--o': 1 }}
        x={x - 4}
        y={y - 4}
        width="8"
        height="8"
        fill="oklch(var(--background))"
        stroke="oklch(var(--foreground) / 0.8)"
        strokeWidth="1.5"
      />
    ))}
    <Parcela retraso={1100} />
  </g>
);

const CAPAS = [
  ['Parcelario catastral', 'oklch(var(--foreground) / 0.45)'],
  ['Curvas de nivel', 'oklch(var(--solar))'],
  ['Hidrografía', 'var(--f-dotacio)'],
  ['Red Natura 2000', 'oklch(var(--disponible))'],
  ['Red eléctrica', 'oklch(var(--foreground) / 0.8)'],
];

const TarjetaUrbanismo = () => (
  <>
    <div className="flex items-baseline justify-between gap-3 border-b border-border px-4 py-3">
      <div>
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Ficha de parcela · ejemplo</div>
        <div className="refcat mt-0.5 text-sm font-medium text-foreground">9872023VH5797S0001WX</div>
      </div>
      <span className="rounded-sm bg-disponible/15 px-1.5 py-0.5 text-[11px] font-medium text-disponible">Citado</span>
    </div>
    <dl>
      {FICHA.slice(0, 4).map(({ campo, valor, fuente }, i) => (
        <div key={campo} className="anim-entra border-b border-border/60 px-4 py-2.5 last:border-b-0" style={{ '--d': `${1100 + i * 160}ms` }}>
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-xs text-muted-foreground">{campo}</dt>
            <dd className="cifra text-right text-sm font-medium text-foreground">{valor}</dd>
          </div>
          <div className="mt-1 flex justify-end">
            <span className="inline-flex items-center gap-1 rounded-sm bg-ember/10 px-1.5 py-0.5 text-[11px] text-[oklch(var(--primary-text))]">
              <FileText className="h-3 w-3" aria-hidden="true" />
              {fuente}
            </span>
          </div>
        </div>
      ))}
    </dl>
  </>
);

const TarjetaTerritorio = () => (
  <>
    <div className="border-b border-border px-4 py-3">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Capas activas</div>
      <div className="mt-0.5 text-sm font-medium text-foreground">Lo que afecta a la parcela</div>
    </div>
    <ul>
      {CAPAS.map(([nombre, color], i) => (
        <li key={nombre} className="anim-entra flex items-center gap-3 border-b border-border/60 px-4 py-2.5 text-sm text-foreground" style={{ '--d': `${300 + i * 160}ms` }}>
          <span className="h-2.5 w-2.5 shrink-0 rounded-[2px]" style={{ background: color }} aria-hidden="true" />
          {nombre}
          <span className="ml-auto h-4 w-7 rounded-full bg-solar/80 p-0.5" aria-hidden="true">
            <span className="block h-3 w-3 translate-x-3 rounded-full bg-background" />
          </span>
        </li>
      ))}
    </ul>
    <div className="anim-entra px-4 py-2.5 text-xs text-muted-foreground" style={{ '--d': '1200ms' }}>
      <span className="cifra">+80</span> capas oficiales · <span className="cifra">15</span> países
    </div>
  </>
);

export default function MapaVivo({ modo }) {
  const urbanismo = modo === 'urbanismo';
  return (
    <figure
      className="relative sm:h-[520px]"
      aria-label={urbanismo ? 'Ejemplo: zonificación urbanística y ficha de una parcela con la cita de cada cifra' : 'Ejemplo: capas territoriales que afectan a una parcela'}
    >
      <div className="relative aspect-square overflow-hidden rounded-lg border border-border bg-[oklch(var(--nivel0))] shadow-md sm:absolute sm:inset-0 sm:right-[16%] sm:aspect-auto">
        <svg viewBox="0 0 520 520" preserveAspectRatio="xMidYMid slice" className="block h-full w-full" aria-hidden="true">
          <Parcelario />
          {urbanismo ? <VistaUrbanismo key="u" /> : <VistaTerritorio key="t" />}
        </svg>
        <span className="absolute left-3 top-3 rounded-sm bg-background/85 px-2 py-1 text-[11px] font-medium text-foreground">
          {urbanismo ? 'Plano de zonificación' : 'Visor territorial'}
        </span>
      </div>
      <div
        key={modo}
        className="relative z-10 mx-3 -mt-12 overflow-hidden rounded-lg border border-border bg-card/95 shadow-lg backdrop-blur-md sm:absolute sm:bottom-10 sm:right-0 sm:mx-0 sm:mt-0 sm:w-[300px]"
      >
        {urbanismo ? <TarjetaUrbanismo /> : <TarjetaTerritorio />}
      </div>
    </figure>
  );
}
